<template>
  <NuxtLayout name="sticky">
    <template #header>
      <CmsHeader v-bind="{ title }" />
    </template>

    <section v-if="page.length" class="cms">
      <ContentfulRenderer
        v-for="(block, i) in page"
        :key="`block-${i}`"
        class="content"
        :data="block.content"
      />
    </section>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type {
  CmsPageQuery,
  ContentBlock,
  Page,
} from '~/@types/generated/cms/graphql-schema-types'

const licensedTerritory = useLicensedTerritory()
const route = useRoute()

const { data: routes } = await useAsyncData(`routes_${route.path}`, () =>
  queryContent<{
    items: Page[]
  }>(`/contentful/pages/routes/${licensedTerritory}`)
    .findOne()
    .catch(),
)

const { data: pageContent } = await useAsyncData(`page_${route.path}`, () => {
  const pageToRender = routes.value?.items.find((page) => {
    if (!page.url) return false
    return route.path.includes(page.url)
  })

  return queryContent<CmsPageQuery['page']>(
    `/contentful/pages/${licensedTerritory}-${pageToRender?.url}`,
  )
    .findOne()
    .catch()
})

const page = computed(
  () =>
    pageContent.value?.contentBlocksCollection?.items.filter(isContentBlock) ??
    [],
)

const title = computed(() => pageContent.value?.title || '')

const isContentBlock = (property: unknown): property is ContentBlock =>
  (property as ContentBlock).__typename === 'ContentBlock'
</script>

<style lang="scss" scoped>
.cms {
  :deep(.content) {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @include token-headline-h5-headline;

      margin-bottom: token('size/space/gap/050');
      scroll-margin-top: 5rem;
    }

    p {
      margin-bottom: token('size/space/gap/200');
    }

    ul {
      margin-bottom: token('size/space/gap/400');
      padding-left: token('size/space/gap/400');
      list-style: disc;

      li {
        &::marker {
          content: '●  ';
          color: token('color/icon/accent/success/success');
        }

        + li {
          margin-top: token('size/space/gap/200');
        }

        p {
          margin-bottom: 0;
        }
      }
    }
  }
}
</style>
